import React from 'react';
import {MediaFile, useProductOptions} from '@shopify/hydrogen';
import type {
  Maybe,
  Image as ImageProps,
  MediaEdge as MediaEdgeType,
  Media,
} from '@shopify/hydrogen/storefront-api-types';
// @ts-expect-error
import {Swiper, SwiperSlide} from 'swiper/react';
// @ts-expect-error
import {Swiper as SwiperClass} from 'swiper/types';
import 'swiper/swiper.min.css';
import {
  ExternalVideo,
  MediaImage,
  Model3d,
  Video,
} from '@shopify/hydrogen/storefront-api-types';
import clsx from 'clsx';

//types
interface ColourOptionsWithThumbnail {
  colour: string;
  thumbnail: Maybe<ImageProps> | undefined;
  available?: boolean;
  sizes?: {size: string | undefined; available: boolean}[];
}

interface Props {
  media: (ExternalVideo | MediaImage | Model3d | Video)[] | undefined;
  allColourOptionsWithThumbnail?: ColourOptionsWithThumbnail[] | undefined;
}
export default function ProductCarousel({
  media,
  allColourOptionsWithThumbnail,
}: Props) {
  const swiperRef = React.useRef<SwiperClass>(null);
  const swiperNavRef = React.useRef<SwiperClass>(null);
  const SwiperCarousel: SwiperClass = Swiper;

  const {selectedOptions} = useProductOptions();
  const selectedColour = selectedOptions?.Colour;
  const selectedVariantThumbnail = allColourOptionsWithThumbnail?.find(
    (c) => c.colour === selectedColour,
  )?.thumbnail;

  const allMediaUrl = media?.map((m) => {
    if (m.__typename === 'MediaImage') {
      return m?.image?.url;
    }
    return;
  });

  const selectedVariantThumbnailIndex = allMediaUrl?.indexOf(
    selectedVariantThumbnail?.url,
  );

  React.useEffect(() => {
    if (swiperRef?.current && swiperNavRef?.current) {
      swiperRef.current.swiper.slideTo(selectedVariantThumbnailIndex);
      swiperNavRef.current.swiper.slideTo(selectedVariantThumbnailIndex);
    }
  }, [selectedVariantThumbnailIndex]);

  return (
    <div className="mb-8 lg:col-span-7 lg:mb-0">
      <div className="grid grid-cols-5">
        <div className="relative col-span-1 hidden h-full overflow-hidden lg:block">
          <div className="absolute top-0 left-0 h-full w-full">
            {/* vertical carousel nav */}
            <SwiperCarousel
              slidesPerView={4}
              ref={swiperNavRef}
              direction={'vertical'}
              className="absolute top-0 left-0 h-full w-full"
              onSlideChange={(swiper: SwiperClass) => {
                const nextSlide = swiper.activeIndex;
                if (swiperRef?.current) {
                  swiperRef.current.swiper.slideTo(nextSlide);
                }
              }}
            >
              {media &&
                media?.map((m, i: number) => {
                  return (
                    <SwiperSlide key={`carousel-nav-${m.id}`}>
                      <div
                        className="cursor-pointer"
                        onClick={() => {
                          swiperRef?.current?.swiper?.slideTo(i);
                        }}
                      >
                        <MediaFile
                          // @ts-expect-error options should accept className
                          className="relative flex w-full shrink-0 grow-0 select-none object-cover"
                          data={m}
                          draggable={false}
                          fetchpriority="high"
                          tabIndex={0}
                        />
                      </div>
                    </SwiperSlide>
                  );
                })}
            </SwiperCarousel>
          </div>
        </div>
        <div className="col-span-5 lg:col-span-4">
          {/* main carousel */}
          <div className="">
            <SwiperCarousel
              slidesPerView={1}
              ref={swiperRef}
              onSlideChange={(swiper: SwiperClass) => {
                const nextSlide = swiper.activeIndex;
                if (swiperNavRef?.current) {
                  swiperNavRef.current.swiper.slideTo(nextSlide);
                }
              }}
            >
              {media &&
                media?.map((m) => {
                  return (
                    <SwiperSlide key={`carousels-slide-${m.id}`}>
                      <MainCarouselSlide m={m} />
                    </SwiperSlide>
                  );
                })}
            </SwiperCarousel>
          </div>
        </div>
        <div className="col-span-5 lg:hidden">
          {/* mobile carousel nav */}
          <SwiperCarousel
            slidesPerView={4}
            ref={swiperNavRef}
            className="absolute top-0 left-0 h-full w-full"
            onSlideChange={(swiper: SwiperClass) => {
              const nextSlide = swiper.activeIndex;
              if (swiperRef?.current) {
                swiperRef.current.swiper.slideTo(nextSlide);
              }
            }}
          >
            {media &&
              media?.map((m, i: number) => {
                return (
                  <SwiperSlide key={`carousel-nav-${m.id}`}>
                    <div
                      className="relative cursor-pointer overflow-hidden"
                      onClick={() => {
                        swiperRef?.current?.swiper?.slideTo(i);
                      }}
                    >
                      <MediaFile
                        // @ts-expect-error options should accept className
                        className="relative flex w-full shrink-0 grow-0 select-none object-cover"
                        data={m}
                        draggable={false}
                        fetchpriority="high"
                        tabIndex={0}
                      />
                    </div>
                  </SwiperSlide>
                );
              })}
          </SwiperCarousel>
        </div>
      </div>
    </div>
  );
}

function MainCarouselSlide({m}: {m: MediaEdgeType['node']}) {
  const [zoom, setZoom] = React.useState({x: 50, y: 50});
  const [isZoomed, setIsZoomed] = React.useState(false);
  const handleMouseMove = (e: React.MouseEvent) => {
    const eventTarget = e.target as HTMLElement;
    const {left, top, width, height} = eventTarget.getBoundingClientRect();
    const x = ((e.pageX - left) / width) * 100;
    const y = ((e.pageY - top) / height) * 100;
    setZoom({x, y});
  };

  if (m.__typename !== 'MediaImage') {
    return null;
  }

  return (
    <div
      className="relative"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsZoomed(true)}
      onMouseLeave={() => setIsZoomed(false)}
    >
      <div
        className={clsx(
          'absolute top-1/2 left-1/2 z-10 h-full w-full -translate-x-1/2 -translate-y-1/2 scale-125 bg-no-repeat duration-100 ease-out transition-opacity',
          isZoomed ? 'opacity-100' : 'opacity-0',
        )}
        style={{
          backgroundImage: `url(${m?.image?.url})`,
          backgroundPositionX: `${zoom.x}%`,
          backgroundPositionY: `${zoom.y}%`,
        }}
      ></div>
      <MediaFile
        // @ts-expect-error options should accept className
        className="relative flex w-full shrink-0 grow-0 select-none object-cover"
        data={m}
        draggable={false}
        fetchpriority="high"
        tabIndex={0}
      />
    </div>
  );
}
